<template>
  <my-case-select-items-base :class="{ 'rejoin' : formerMember}">
    <template
      #header>
      <div
        v-for="offer in shippingOffers"
        :key="offer.name"
        class="header-content small-text">
        <p class="mb-0">
          FREE <b>{{ offer.name }}</b> shipping with a purchase of {{ formatPrice(offer.minimum) }} or more!
        </p>
      </div>
    </template>
    <template #above-items>
      <div
        v-if="!isHoliday && numStandardSlotsAvailable > 0"
        class="w-100 d-flex flex-column align-items-center pt-4">
        <p class="small-text mb-0">
          {{ isFirstMonth ? 'Need help?' : 'Want a stylist to select for you?' }}
        </p>
        <text-link
          class="small-text mb-3"
          tracking-name="Request Stylist Case"
          :tracking-data="{ isFirstMonth }"
          :to="isFirstMonth ? 'https://armoire.as.me/style-my-case' : null"
          @click="() => !isFirstMonth && setMyCaseFlyoutName('request-stylist-case-one')">
          {{ isFirstMonth ? 'Book a Stylist Call' : 'Request Stylist Case' }}
        </text-link>
      </div>
      <div
        v-if="unlimitedTrialActive"
        class="bg-orchid text-white bold text-center px-xl py-sm">
        Enjoy unlimited swaps this month.
      </div>
      <div
        v-if="hasRentalPlan && numExtraSlotsValid >= 0 && numExtraSlotsValid < maxNumExtraItems"
        class="px-4 px-md-5">
        <my-case-item
          variant="add"
          hide-price
          can-edit>
          Add an extra item for just {{ formatPrice(extraItemBasePrice, true) }}
        </my-case-item>
      </div>
    </template>

    <template #below-standard-items>
      <template v-if="selectedStandardItems.length === 0 && casePurchaseItems.length === 0">
        <ExampleCaseSection
          v-if="formerMember"
          :plan-id="latestSubscriptionPlanId"
          source="case"
          :title="exampleCaseTitle"
          always-show-title
          always-show-two-rows/>
        <div
          v-else
          class="small-text">
          Items you add to your {{ itemContainerText }} will display here
        </div>
      </template>
      <div
        v-for="index in numStandardSlotsAvailable"
        :key="`items-empty-${index}`">
        <my-case-item
          variant="add"
          hide-price
          can-edit>
          <p v-if="index <= numRolloverSlotsAvailable">
            <sequin-tag
              class="align-self-end justify-self-start mb-1"
              variant="darkGray">
              ROLLOVER ITEM
            </sequin-tag>
            <br>
            Expires in {{ getRolloverItemExpireDays(index) }}
          </p>
        </my-case-item>
      </div>

      <my-case-item
        v-for="item in selectedBonusItems"
        :key="item.id"
        can-edit
        hide-price
        :use-large-edit-button="!isMobile"
        :item="item">
        <template #itemInfoSlot>
          <sequin-tag
            light
            variant="secondary">
            <div class="d-flex align-items-center">
              <svg-star
                height="10"
                width="10"
                class="mx-0 me-1"/>
              Bonus Item
            </div>
          </sequin-tag>
        </template>
      </my-case-item>
      <my-case-bonus-item-slots/>
    </template>

    <template #end-of-section>
      <div
        v-if="primaryCaseState !== 'inactive-member'"
        class="d-flex"
        :class="{'flex-column': isMobile }">
        <sequin-button
          :disabled="tooFewSelected || numExtraSlotsOverLimit > 0 || confirmingSelectedItems"
          block
          :class="{ 'ms-1': !isMobile }"
          @click="continueToConfirm">
          Continue to Confirm
        </sequin-button>
      </div>
    </template>
  </my-case-select-items-base>
</template>

<script>
import MyCaseSelectItemsBase from '../common/MyCaseSelectItemsBase'
import MyCaseBonusItemSlots from './MyCaseBonusItemSlots'
import MyCaseMixin from '../common/MyCaseMixin'
import MyCaseItem from '../common/MyCaseItem'
import SequinTag from '../../global/sequin/SequinTag'
import SequinButton from '../../global/sequin/SequinButton'
import { formatPrice, toMonthDayYearString } from '@/utils/stringParsing.js'
import TextLink from '@shared/components/TextLink.vue'
import { mapActions as mapVuexActions, mapState as mapVuexState, mapGetters } from 'vuex'
import { storeToRefs } from 'pinia'
import useAnalytics from '@shared/composables/analytics.js'
import SvgStar from '@/components/global/svg/SvgStar.vue'
import useHolidays from '@shared/composables/holidays.js'
import ExampleCaseSection from '@/components/signUp/ExampleCaseSection.vue'
import { useRejoinStore } from '@/stores/rejoin.js'
import { usePlans } from '@shared/composables/plans.js'
import { useUnlimitedTrialStore } from '@/stores/unlimitedTrial.js'

export default {
  components: {
    TextLink,
    MyCaseItem,
    MyCaseBonusItemSlots,
    SequinTag,
    SequinButton,
    MyCaseSelectItemsBase,
    SvgStar,
    ExampleCaseSection
  },
  mixins: [MyCaseMixin],
  setup () {
    const { isHoliday } = useHolidays()
    const { track } = useAnalytics()
    const { latestSubscriptionPlanId, formerMember } = storeToRefs(useRejoinStore())
    const { fourItemPlan, sevenItemPlan, unlimitedPlan } = usePlans()
    const { unlimitedTrialActive } = storeToRefs(useUnlimitedTrialStore())
    return {
      isHoliday,
      track,
      latestSubscriptionPlanId,
      formerMember,
      fourItemPlan,
      sevenItemPlan,
      unlimitedPlan,
      unlimitedTrialActive,
    }
  },
  data () {
    return {
      confirmingSelectedItems: false
    }
  },
  computed: {
    ...mapGetters('case', [
      'nextFlyout'
    ]),
    ...mapGetters('client', [
      'itemContainerText',
      'isFirstMonth'
    ]),
    ...mapVuexState('case', [
      'shippingOffers'
    ]),
    ...mapVuexState('client', [
      'returnCarrierPreference'
    ]),
    exampleCaseTitle () {
      if (this.latestSubscriptionPlanId === this.fourItemPlan.id) {
        return 'Sample 4-Item Case'
      } else if (this.latestSubscriptionPlanId === this.sevenItemPlan.id) {
        return 'Sample 7-Item Case'
      } else if (this.latestSubscriptionPlanId === this.unlimitedPlan.id) {
        return 'Unlimited Case'
      }
      return ''
    }
  },
  mounted () {
    this.track('Viewed My Case')
  },
  methods: {
    ...mapVuexActions('case', [
      'setMyCaseFlyoutName'
    ]),
    formatPrice,
    toMonthDayYearString,
    async continueToConfirm () {
      this.confirmingSelectedItems = true
      await this.checkForSelectedItemsMismatch()
      if (this.selectedItemsMismatch || this.unavailableItemTypes.length > 0) {
        this.confirmingSelectedItems = false
      } else {
        this.setMyCaseFlyoutName(this.nextFlyout)
      }
    },
    continueToRequestStylistCase () {
      if (!this.isHoliday) {
        if (this.isFirstMonth) {
          window.open('https://armoire.as.me/style-my-case', '_blank')
        } else {
          this.setMyCaseFlyoutName('request-stylist-case-one')
        }
      }
    },
    getRolloverItemExpireDays (index) {
      const rolloverItem = this.rolloverItems[index - 1]
      const today = new Date()
      const rolloverExpireDate = new Date(rolloverItem.expires)
      const diff = rolloverExpireDate.getTime() - today.getTime()
      const days = Math.floor(diff / (1000 * 60 * 60 * 24))
      return `${days} day${days > 1 ? 's' : ''}`
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.rejoin.items-container) {
  padding: 0 !important;
}
:deep(.example-case-section) {
  text-transform: uppercase;
  text-align: center;
  @include media-tablet-or-smaller {
    justify-content: start;
  }
  .title {
    font-weight: $font-weight-semibold;
  }
}
</style>
